import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Lock, ArrowRight, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [tempToken, setTempToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [useBackup, setUseBackup] = useState(false);

  const go = (user) => navigate(user.role === "field_agent" ? "/arama" : "/panel");

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        setTempToken(data.temp_token);
      } else {
        login(data.token, data.user);
        go(data.user);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-2fa", { temp_token: tempToken, code });
      login(data.token, data.user);
      go(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Doğrulama başarısız");
    } finally {
      setLoading(false);
    }
  };

  const fieldCls = "bg-white/[0.06] border-white/10 text-white placeholder:text-white/25 focus-visible:ring-cyan-400/60 focus-visible:border-cyan-400/40 h-11";

  return (
    <div className="min-h-screen w-full relative overflow-hidden flex items-center justify-center p-6 bg-[#081428]" data-testid="login-page">
      {/* ambient depth */}
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(60% 50% at 50% 0%, rgba(34,211,238,0.10), transparent 70%), radial-gradient(50% 60% at 85% 100%, rgba(30,58,138,0.35), transparent 70%)" }} />
      <div className="pointer-events-none absolute -top-40 -left-40 h-96 w-96 rounded-full bg-cyan-500/10 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-48 -right-32 h-[28rem] w-[28rem] rounded-full bg-blue-900/40 blur-[130px]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />

      <div className="relative z-10 w-full max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-2xl p-8">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-700 grid place-items-center shadow-lg shadow-cyan-500/20 ring-1 ring-white/20">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <p className="mt-5 text-[11px] font-semibold tracking-[0.35em] text-cyan-300/70 uppercase">Yetkili Girişi</p>
            <span className="mt-2 h-px w-10 bg-white/15" />
          </div>

          {!tempToken ? (
            <form onSubmit={submitLogin} className="space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/70 text-xs">Kullanıcı Adı</Label>
                <Input id="email" type="text" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="" required data-testid="login-email-input" className={fieldCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/70 text-xs">Şifre</Label>
                <div className="relative">
                  <Input id="password" type={showPw ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="" required data-testid="login-password-input" className={`${fieldCls} pr-11`} />
                  <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Şifreyi gizle" : "Şifreyi göster"} data-testid="toggle-password-visibility" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {error && <p className="text-sm text-red-300" data-testid="login-error">{error}</p>}
              <Button type="submit" className="w-full h-11 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold shadow-lg shadow-cyan-500/20 group" disabled={loading} data-testid="login-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Giriş <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" /></>}
              </Button>
            </form>
          ) : (
            <form onSubmit={submit2FA} className="space-y-5" data-testid="twofa-form">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-7 w-7 text-cyan-300" />
                <p className="text-sm text-white/70">{useBackup ? "Yedek kurtarma kodunuzu girin" : "6 haneli doğrulama kodunu girin"}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="code" className="text-white/70 text-xs">{useBackup ? "Yedek Kod" : "Doğrulama Kodu"}</Label>
                {useBackup ? (
                  <Input id="code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))} maxLength={9} placeholder="XXXX-XXXX" className={`${fieldCls} text-center text-xl tracking-[0.3em] font-mono`} required data-testid="twofa-backup-input" />
                ) : (
                  <Input id="code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" className={`${fieldCls} text-center text-2xl tracking-[0.5em] font-mono`} required data-testid="twofa-code-input" />
                )}
              </div>
              {error && <p className="text-sm text-red-300" data-testid="twofa-error">{error}</p>}
              <Button type="submit" className="w-full h-11 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold" disabled={loading} data-testid="twofa-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Doğrula ve Giriş"}
              </Button>
              <button type="button" onClick={() => { setUseBackup((b) => !b); setCode(""); setError(""); }} className="w-full text-xs text-cyan-300/70 hover:text-cyan-200 transition-colors" data-testid="twofa-toggle-backup">
                {useBackup ? "Doğrulama uygulaması kodunu kullan" : "Cihazınıza erişemiyor musunuz? Yedek kod kullanın"}
              </button>
              <Button type="button" variant="ghost" className="w-full text-white/60 hover:text-white hover:bg-white/5" onClick={() => { setTempToken(null); setCode(""); setError(""); setUseBackup(false); }} data-testid="twofa-back-button">
                Geri Dön
              </Button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-[10px] tracking-widest text-white/20 uppercase select-none">• • •</p>
      </div>
    </div>
  );
}
